// src/features/tuition/components/online/TopicForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Autocomplete,
  Chip,
  FormHelperText,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem, 
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { 
  createTopic, 
  updateTopic, 
  fetchTopicsForSubject 
} from '../../services/subjectsService';

const TopicForm = ({ open, onClose, onSubmit, initialData, subjectId, userId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    preknowledge: '',
    skills: '',
    relatedTopics: []
  });
  
  const [allTopics, setAllTopics] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  useEffect(() => {
    if (open) {
      loadTopics();
      loadSubjects(); // Add this function to load subjects
      
      if (initialData) {
        setFormData({
          name: initialData.name || '',
          description: initialData.description || '',
          preknowledge: initialData.preknowledge || '',
          skills: initialData.skills || '',
          relatedTopics: initialData.relatedTopics || [],
          subjectId: initialData.subjectId || subjectId || '' // Use provided subjectId as fallback
        });
      } else {
        resetForm();
      }
    }
  }, [initialData, open, subjectId]);
  
  const loadTopics = async () => {
    try {
      const topics = await fetchTopicsForSubject(subjectId);
      
      // If we are editing, remove the current topic from the list of possible related topics
      const filteredTopics = initialData
        ? topics.filter(topic => topic.id !== initialData.id)
        : topics;
      
      setAllTopics(filteredTopics);
    } catch (error) {
      console.error("Error loading topics:", error);
    }
  };

  const loadSubjects = async () => {
    try {
      // This would typically be an API call
      // For example: const subjects = await fetchSubjects(userId);
      
      // Mock data for demonstration
      const subjects = [
        { id: subjectId, name: "Current Subject" },
        { id: "subject2", name: "Mathematics" },
        { id: "subject3", name: "Science" }
      ];
      
      setAvailableSubjects(subjects);
    } catch (error) {
      console.error("Error loading subjects:", error);
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      preknowledge: '',
      skills: '',
      relatedTopics: [],
      subjectId: subjectId || '' // Use provided subjectId as default
    });
    setErrors({});
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleRelatedTopicsChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      relatedTopics: newValue
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Topic name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.subjectId) {
      newErrors.subjectId = 'Subject is required';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const topicData = {
        name: formData.name,
        description: formData.description,
        preknowledge: formData.preknowledge,
        skills: formData.skills,
        relatedTopics: formData.relatedTopics,
        subjectId,
        userId
      };
      
      if (initialData && initialData.id) {
        // Update existing topic
        await updateTopic(initialData.id, topicData);
        
        setNotification({
          open: true,
          message: 'Topic updated successfully!',
          severity: 'success'
        });
      } else {
        // Create new topic
        await createTopic(topicData);
        
        setNotification({
          open: true,
          message: 'Topic created successfully!',
          severity: 'success'
        });
      }
      
      // Call onSubmit callback
      if (onSubmit) {
        onSubmit(topicData);
      }
      
      resetForm();
      
    } catch (error) {
      console.error("Error saving topic:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? null : onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ 
          sx: { borderRadius: 2 } 
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {initialData ? 'Edit Topic' : 'Add New Topic'}
            </Typography>
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={onClose}
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Topic Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              error={!!errors.name}
              helperText={errors.name}
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              required
              error={!!errors.description}
              helperText={errors.description}
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <FormControl 
            fullWidth 
            error={!!errors.subjectId} 
            sx={{ mb: 3 }}
            disabled={loading || !!subjectId}
            >
            <InputLabel>Subject</InputLabel>
            <Select
                name="subjectId"
                value={formData.subjectId}
                label="Subject"
                onChange={handleInputChange}
                required
            >
                <MenuItem value="">
                <em>Select a subject</em>
                </MenuItem>
                {availableSubjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                </MenuItem>
                ))}
            </Select>
            {errors.subjectId && <FormHelperText>{errors.subjectId}</FormHelperText>}
            {!!subjectId && <FormHelperText>Subject selected from context</FormHelperText>}
            </FormControl>
                
            <TextField
              fullWidth
              label="Pre-knowledge (Optional)"
              name="preknowledge"
              value={formData.preknowledge}
              onChange={handleInputChange}
              multiline
              rows={2}
              placeholder="What should students already know before starting this topic?"
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Required Skills (Optional)"
              name="skills"
              value={formData.skills}
              onChange={handleInputChange}
              multiline
              rows={2}
              placeholder="What skills should students already have before starting this topic?"
              disabled={loading}
              sx={{ mb: 3 }}
            />
            
            <Autocomplete
              multiple
              options={allTopics}
              getOptionLabel={(option) => option.name}
              value={formData.relatedTopics}
              onChange={handleRelatedTopicsChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Related Topics (Optional)"
                  placeholder="Select related topics"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                ))
              }
              disabled={loading}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': { bgcolor: '#1e1c4e' }
            }}
          >
            {loading ? 'Saving...' : initialData ? 'Update Topic' : 'Add Topic'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TopicForm;